import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import {
  FileText, Check, X, Loader2, Download, Trash2, Search, Filter, 
  Folder, FileSignature, AlertCircle, FileDigit, Calendar, Eye
} from 'lucide-react'

import JSZip from 'jszip'
import { saveAs } from 'file-saver'

interface Document {
  id: string
  nurse_id: string
  interview_id?: string
  nome: string
  categoria: string
  url: string
  file_size?: number
  created_at: string
  nurses?: { nome: string, cognome: string }
  interviews?: { nome: string, cognome: string }
  patients?: { nome: string, cognome: string }
}

const CATEGORIES = [
  { key: 'cv', label: 'CV', icon: <FileText className="w-4 h-4" />, color: 'text-blue-600 bg-blue-50' },
  { key: 'contratto', label: 'Contratto', icon: <FileSignature className="w-4 h-4" />, color: 'text-indigo-600 bg-indigo-50' },
  { key: 'assicurazione', label: 'Assicurazione', icon: <FileDigit className="w-4 h-4" />, color: 'text-emerald-600 bg-emerald-50' },
  { key: 'albo', label: 'Albo', icon: <Folder className="w-4 h-4" />, color: 'text-amber-600 bg-amber-50' },
  { key: 'altro', label: 'Altro', icon: <FileText className="w-4 h-4" />, color: 'text-slate-600 bg-slate-50' },
]

export function DocumentsExplorer() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isZipping, setIsZipping] = useState(false)

  useEffect(() => { fetchDocuments() }, [])

  async function fetchDocuments() {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('documents')
      .select('*, nurses(nome, cognome), interviews(nome, cognome), patients(nome, cognome)')
      .order('created_at', { ascending: false })

    if (err) setError(err.message)
    else setDocuments(data as Document[])
    setLoading(false)
  }

  async function handleDelete(id: string, url: string) {
    if (!confirm('Eliminare questo documento definivamente?')) return
    
    try {
      // Estrarre il percorso dal URL (assumiamo nome bucket: documents)
      const pathMatch = url.match(/\/documents\/(.+)$/)
      if (pathMatch) {
         await supabase.storage.from('documents').remove([pathMatch[1]])
      }
      await supabase.from('documents').delete().eq('id', id)
      setDocuments(documents.filter(d => d.id !== id))
      setSuccess('Documento rimosso')
    } catch (e: any) {
      setError(e.message)
    }
  }

  async function handleDownloadZip() {
    if (selectedDocs.size === 0) return
    setIsZipping(true)
    try {
      const zip = new JSZip()
      const docsToZip = documents.filter(d => selectedDocs.has(d.id))
      
      for (const doc of docsToZip) {
        // Scarichiamo il file, ma JSZip può necessitare del blob
        const res = await fetch(doc.url)
        const blob = await res.blob()
        const personName = doc.nurses ? `${doc.nurses.cognome}_${doc.nurses.nome}` 
                           : doc.patients ? `${doc.patients.cognome}_${doc.patients.nome}`
                           : doc.interviews ? `${doc.interviews.cognome}_${doc.interviews.nome}` : 'Sconosciuto'
        const safeName = doc.nome.replace(/[^a-zA-Z0-9.-]/g, '_')
        zip.file(`${personName}/${doc.categoria}_${safeName}`, blob)
      }
      
      const content = await zip.generateAsync({ type: 'blob' })
      saveAs(content, `QuiCare_Documenti_${new Date().toISOString().slice(0, 10)}.zip`)
      setSuccess('Download ZIP completato!')
      setSelectedDocs(new Set())
    } catch (e: any) {
      setError('Errore download: ' + e.message)
    } finally {
      setIsZipping(false)
    }
  }

  const toggleSelect = (id: string) => {
    const newSet = new Set(selectedDocs)
    if (newSet.has(id)) newSet.delete(id)
    else newSet.add(id)
    setSelectedDocs(newSet)
  }

  const toggleSelectAll = () => {
    if (selectedDocs.size === filtered.length) setSelectedDocs(new Set())
    else setSelectedDocs(new Set(filtered.map(d => d.id)))
  }

  const filtered = documents.filter(d => {
    const matchCat = categoryFilter === 'all' || d.categoria === categoryFilter
    const searchStr = search.toLowerCase()
    
    // Controlliamo il nome del file
    const matchName = d.nome.toLowerCase().includes(searchStr)
    
    // Controlliamo i nomi associati
    const matchPerson = (d.nurses && (`${d.nurses.nome} ${d.nurses.cognome}`.toLowerCase().includes(searchStr))) ||
                        (d.interviews && (`${d.interviews.nome} ${d.interviews.cognome}`.toLowerCase().includes(searchStr))) ||
                        (d.patients && (`${d.patients.nome} ${d.patients.cognome}`.toLowerCase().includes(searchStr)))
    
    return matchCat && (matchName || matchPerson)
  })

  if (loading) return <div className="p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>

  return (
    <div className="space-y-6 animate-fade-in-up">
      {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl">{error}</div>}
      {success && <div className="p-4 bg-green-50 text-green-700 rounded-xl">{success}</div>}

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2">
            <Folder className="w-6 h-6 text-indigo-500" /> Archivio File
          </h2>
          <p className="text-sm text-slate-500 mt-1">Gestisci tutti i documenti caricati ({documents.length} file trovati)</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
             <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
             <input type="text" placeholder="Cerca file o persona..." value={search} onChange={e => setSearch(e.target.value)} 
                className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none w-64" />
          </div>
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none">
            <option value="all">Tutte le Categorie</option>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {selectedDocs.size > 0 && (
            <button onClick={handleDownloadZip} disabled={isZipping} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg disabled:opacity-50">
              {isZipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} SCARICA ZIP ({selectedDocs.size})
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-xs">
              <th className="px-4 py-3 w-10 text-center"><input type="checkbox" checked={filtered.length > 0 && selectedDocs.size === filtered.length} onChange={toggleSelectAll} className="w-4 h-4 rounded text-indigo-500 border-slate-300 focus:ring-indigo-500" /></th>
              <th className="px-4 py-3 text-left">File</th>
              <th className="px-4 py-3 text-left">Persona collegata</th>
              <th className="px-4 py-3 text-left">Data Caricamento</th>
              <th className="px-4 py-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(doc => {
              const catObj = CATEGORIES.find(c => c.key === doc.categoria) || CATEGORIES[4]
              const refName = doc.nurses ? `${doc.nurses.cognome} ${doc.nurses.nome}`
                              : doc.patients ? `${doc.patients.cognome} ${doc.patients.nome}`
                              : doc.interviews ? `${doc.interviews.cognome} ${doc.interviews.nome}` : 'Non assegnato'
              const isCandidate = !!doc.interview_id
              const isPatient = !!doc.patients
              const isSelected = selectedDocs.has(doc.id)
              
              return (
                <tr key={doc.id} className={`hover:bg-slate-50/50 transition-colors ${isSelected ? 'bg-indigo-50/30' : ''}`}>
                  <td className="px-4 py-4 text-center">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(doc.id)} className="w-4 h-4 rounded text-indigo-500 border-slate-300 focus:ring-indigo-500" />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${catObj.color}`}>{catObj.icon}</div>
                      <div>
                        <div className="font-bold text-slate-700 truncate max-w-[200px]" title={doc.nome}>{doc.nome}</div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{catObj.label}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-700">{refName}</span>
                      {isCandidate && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold uppercase">Candidato</span>}
                      {isPatient && <span className="text-[10px] bg-sky-100 text-sky-600 px-1.5 py-0.5 rounded font-bold uppercase">Paziente</span>}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-slate-500 tabular-nums">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> {new Date(doc.created_at).toLocaleDateString('it-IT')}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                       <a href={doc.url} target="_blank" rel="noreferrer" className="p-2 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg" title="Apri / Visualizza"><Eye className="w-4 h-4" /></a>
                       <a href={doc.url} download target="_blank" rel="noreferrer" className="p-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded-lg" title="Scarica"><Download className="w-4 h-4" /></a>
                       <button onClick={() => handleDelete(doc.id, doc.url)} className="p-2 bg-red-50 text-red-500 hover:bg-red-100 rounded-lg" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center text-slate-400">Nessun documento trovato.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
